import { useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, ShoppingBag, LayoutGrid, List } from 'lucide-react';
import { useShopCollection, useShopCollections } from '../../hooks/useShopify';
import { formatShopifyMoney, shopifyConfigError, type ShopProduct } from '../../lib/shopify';
import { useShopStore } from '../../store/shopStore';

interface ShopCollectionScreenProps {
  collectionHandle: string | null;
  onNavigate: (screen: any, params?: any) => void;
}

export default function ShopCollectionScreen({ collectionHandle, onNavigate }: ShopCollectionScreenProps) {
  const {
    collection, error, isLoading, isConfigured,
    isLoadingMore, hasNextPage, loadMore,
  } = useShopCollection(collectionHandle);
  const { collections } = useShopCollections();
  const cartCount = useShopStore((state) => state.lines.reduce((sum, line) => sum + line.quantity, 0));
  const [view, setView] = useState<'grid' | 'list'>('grid');

  if (!isConfigured) {
    return (
      <StateScreen
        title="Shop not configured"
        body={shopifyConfigError ?? 'Shopify env vars are missing.'}
        onBack={() => onNavigate('shop')}
      />
    );
  }

  if (!collectionHandle) {
    return (
      <StateScreen
        title="Collection not found"
        body="Open the shop again and choose a collection."
        onBack={() => onNavigate('shop')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Loading collection…
      </div>
    );
  }

  if (error || !collection) {
    return (
      <StateScreen
        title="Could not load collection"
        body={error?.message ?? 'This collection is unavailable.'}
        onBack={() => onNavigate('shop')}
      />
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('shop')}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg truncate">{collection.title}</h1>
          <p className="text-xs text-muted-foreground">Shop collection</p>
        </div>
        <button
          onClick={() => onNavigate('shop-cart')}
          className="relative w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
          aria-label="Open cart"
        >
          <ShoppingBag className="w-5 h-5" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#1A6B4F] text-white text-[10px] flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>
      </div>

      <div className="px-6 py-6 space-y-5">
        <div className="overflow-hidden rounded-[2rem] border border-border bg-card">
          <div className="h-56 bg-secondary">
            {collection.image ? (
              <img src={collection.image.url} alt={collection.image.altText ?? collection.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[radial-gradient(circle_at_top_left,_rgba(198,248,6,0.24),_transparent_35%),linear-gradient(135deg,#1A6B4F_0%,#18260d_100%)]" />
            )}
          </div>
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#1A6B4F]">Collection</div>
            <div className="text-3xl leading-tight mt-2">{collection.title}</div>
            <div className="text-sm text-muted-foreground mt-3 max-w-2xl">
              {collection.description || 'Browse the products currently published in this Shopify collection.'}
            </div>
          </div>
        </div>

        {collections.length > 1 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {collections.map((entry) => (
              <button
                key={entry.id}
                onClick={() => onNavigate('shop-collection', { collectionHandle: entry.handle })}
                className={`h-9 whitespace-nowrap rounded-full px-4 text-sm transition-colors ${
                  entry.handle === collection.handle
                    ? 'bg-[#1A6B4F] text-white'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {entry.title}
              </button>
            ))}
          </div>
        )}

        {collection.products.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            No products are currently published in this collection.
          </div>
        ) : (
          <>
            {/* Count + view toggle. Toggle persists for the session
                only — picking a default that fits each customer's
                browsing style without preference plumbing. */}
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {collection.products.length} product{collection.products.length === 1 ? '' : 's'}
              </div>
              <div className="inline-flex rounded-full border border-[#E7E9E2] bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  aria-label="Grid view"
                  aria-pressed={view === 'grid'}
                  className={`h-7 px-2.5 rounded-full inline-flex items-center gap-1 text-xs transition-colors ${
                    view === 'grid' ? 'bg-[#1A6B4F] text-white' : 'text-[#5F665B] hover:text-[#0F1E36]'
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Grid
                </button>
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-label="List view"
                  aria-pressed={view === 'list'}
                  className={`h-7 px-2.5 rounded-full inline-flex items-center gap-1 text-xs transition-colors ${
                    view === 'list' ? 'bg-[#1A6B4F] text-white' : 'text-[#5F665B] hover:text-[#0F1E36]'
                  }`}
                >
                  <List className="w-3.5 h-3.5" />
                  List
                </button>
              </div>
            </div>

            {view === 'grid' ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {collection.products.map((product) => (
                  <CollectionProductGridCard
                    key={product.id}
                    product={product}
                    onOpen={() => onNavigate('shop-product', { handle: product.handle })}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {collection.products.map((product) => (
                  <CollectionProductListRow
                    key={product.id}
                    product={product}
                    onOpen={() => onNavigate('shop-product', { handle: product.handle })}
                  />
                ))}
              </div>
            )}

            {/* Load more — only renders when Shopify reports another page.
                Cursor pagination via pageInfo.endCursor; appends to the
                existing list rather than replacing it. */}
            {hasNextPage && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="w-full h-12 rounded-xl border-2 border-[#0F1E36] text-[#0F1E36] text-sm font-semibold hover:bg-[#0F1E36] hover:text-white active:bg-[#0A1628] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Compact grid tile. 2-cols on mobile, scales up to 4-cols on large.
 * Description is dropped here since it doesn't fit cleanly at this
 * size — the list view is for skim-with-context.
 */
function CollectionProductGridCard({
  product,
  onOpen,
}: {
  product: ShopProduct;
  onOpen: () => void;
}) {
  const image = product.featuredImage ?? product.images[0] ?? null;
  const isInStock = product.variants.some((variant) => variant.availableForSale);

  return (
    <button
      onClick={onOpen}
      className="overflow-hidden rounded-2xl border border-border bg-card text-left transition-colors hover:border-[#1A6B4F]/40 flex flex-col"
    >
      <div className="relative aspect-square bg-secondary">
        {image ? (
          <img src={image.url} alt={image.altText ?? product.title} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">No image</div>
        )}
        {!isInStock && (
          <span className="absolute top-2 left-2 rounded-full bg-white/95 px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] text-[#5F665B]">
            Out of stock
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <div className="text-[13px] leading-tight line-clamp-2 font-medium text-[#0F1E36]">
          {product.title}
        </div>
        <div className="mt-auto text-sm font-semibold text-[#1A6B4F]">
          {formatShopifyMoney(product.priceRange.minVariantPrice)}
        </div>
      </div>
    </button>
  );
}

/**
 * Horizontal list row — square thumb on the left, title/description/
 * price on the right. One per line so the row is comfortable to read
 * even with a long description.
 */
function CollectionProductListRow({
  product,
  onOpen,
}: {
  product: ShopProduct;
  onOpen: () => void;
}) {
  const image = product.featuredImage ?? product.images[0] ?? null;
  const isInStock = product.variants.some((variant) => variant.availableForSale);

  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-card border border-border rounded-2xl p-3 flex gap-3 hover:border-[#1A6B4F]/40 transition-colors"
    >
      <div className="w-20 h-20 rounded-xl bg-secondary overflow-hidden flex-shrink-0 flex items-center justify-center">
        {image ? (
          <img src={image.url} alt={image.altText ?? product.title} className="h-full w-full object-cover" />
        ) : (
          <span className="text-[10px] text-muted-foreground">No image</span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="text-sm font-medium leading-tight line-clamp-1 text-[#0F1E36]">
          {product.title}
        </div>
        {product.description && (
          <div className="text-xs text-muted-foreground line-clamp-2">
            {product.description}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-[#1A6B4F]">
            From {formatShopifyMoney(product.priceRange.minVariantPrice)}
          </span>
          {!isInStock && (
            <span className="text-[10px] uppercase tracking-[0.12em] text-[#5F665B]">
              Out of stock
            </span>
          )}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 self-center shrink-0 text-muted-foreground" />
    </button>
  );
}

function StateScreen({
  title,
  body,
  onBack,
}: {
  title: string;
  body: string;
  onBack: () => void;
}) {
  return (
    <div className="w-full min-h-screen bg-background flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="text-xl mb-2">{title}</div>
        <div className="text-sm text-muted-foreground mb-5">{body}</div>
        <button onClick={onBack} className="h-11 px-5 rounded-xl bg-[#1A6B4F] text-white text-sm">
          Back to shop
        </button>
      </div>
    </div>
  );
}